import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Plus, Save, Eye, Send, Trash2, GripVertical, Edit2, Copy, 
  ChevronUp, ChevronDown, Image, Youtube, Type, LayoutGrid,
  Trophy, Link2, Tv, MapPin, Clock, BarChart2, MessageSquare,
  Settings, History, ArrowLeft, Check, X, AlertCircle, Upload, Loader2,
  FolderOpen, Search, Grid, List, ImageIcon, CheckCircle, XCircle, Files
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// Bulk Upload Progress Component
// ============================================
const BulkUploadProgress = ({ uploads, onComplete }) => {
  const completed = uploads.filter(u => u.status === 'complete').length;
  const failed = uploads.filter(u => u.status === 'error').length;
  const total = uploads.length;
  const allDone = completed + failed === total;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-blue-800 flex items-center gap-2">
          <Files className="w-4 h-4" />
          Uploading {total} image{total !== 1 ? 's' : ''}
        </h4>
        <span className="text-sm text-blue-600">
          {completed}/{total} complete
          {failed > 0 && <span className="text-red-600 ml-2">({failed} failed)</span>}
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${((completed + failed) / total) * 100}%` }}
        />
      </div>

      {/* File list */}
      <div className="max-h-32 overflow-y-auto space-y-1">
        {uploads.map((upload, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            {upload.status === 'uploading' && (
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            )}
            {upload.status === 'complete' && (
              <CheckCircle className="w-4 h-4 text-green-500" />
            )}
            {upload.status === 'error' && (
              <XCircle className="w-4 h-4 text-red-500" />
            )}
            {upload.status === 'pending' && (
              <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
            )}
            <span className={`truncate flex-1 ${upload.status === 'error' ? 'text-red-600' : ''}`}>
              {upload.name}
            </span>
            {upload.status === 'error' && (
              <span className="text-xs text-red-500">{upload.error}</span>
            )}
          </div>
        ))}
      </div>

      {allDone && (
        <button
          onClick={onComplete}
          className="mt-3 w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Done - View Library
        </button>
      )}
    </div>
  );
};

// ============================================
// Media Library Component with Bulk Upload
// ============================================
const MediaLibrary = ({ isOpen, onClose, onSelect }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]); // For tracking bulk uploads
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    }
  }, [isOpen]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/files`);
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Failed to load media library:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk file upload
  const handleBulkUpload = async (fileList) => {
    const filesToUpload = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    
    if (filesToUpload.length === 0) {
      toast.error('No valid image files selected');
      return;
    }

    // Initialize upload tracking
    const uploadStates = filesToUpload.map(f => ({
      name: f.name,
      file: f,
      status: 'pending', // pending, uploading, complete, error
      error: null,
      result: null
    }));
    
    setUploads(uploadStates);
    setIsUploading(true);
    setShowUploadArea(false);

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < uploadStates.length; i++) {
      const upload = uploadStates[i];
      
      // Update status to uploading
      setUploads(prev => prev.map((u, idx) => 
        idx === i ? { ...u, status: 'uploading' } : u
      ));

      try {
        // Validate file size
        if (upload.file.size > 5 * 1024 * 1024) {
          throw new Error('File too large (max 5MB)');
        }

        const formData = new FormData();
        formData.append('file', upload.file);

        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Update status to complete
        setUploads(prev => prev.map((u, idx) => 
          idx === i ? { ...u, status: 'complete', result: response.data } : u
        ));
      } catch (error) {
        // Update status to error
        setUploads(prev => prev.map((u, idx) => 
          idx === i ? { 
            ...u, 
            status: 'error', 
            error: error.response?.data?.detail || error.message || 'Upload failed'
          } : u
        ));
      }
    }
  };

  // Handle upload completion
  const handleUploadComplete = () => {
    const successCount = uploads.filter(u => u.status === 'complete').length;
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount !== 1 ? 's' : ''} uploaded successfully`);
    }
    setUploads([]);
    setIsUploading(false);
    loadFiles(); // Refresh the library
  };

  // Drag handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleBulkUpload(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleBulkUpload(files);
    }
  };

  const filteredFiles = files.filter(f => 
    f.original_filename?.toLowerCase().includes(search.toLowerCase()) ||
    f.alt_text?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-[#00204E] flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Media Library
          </h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowUploadArea(!showUploadArea)}
              className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-1.5 ${
                showUploadArea 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-[#00204E] text-white hover:bg-[#003366]'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload Images
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Bulk Upload Area */}
        {showUploadArea && !isUploading && (
          <div className="p-4 border-b bg-gray-50">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <div
              ref={dropZoneRef}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 text-blue-600' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600'
              }`}
            >
              <Upload className="w-12 h-12 mx-auto mb-3" />
              <p className="font-semibold text-lg">
                {isDragging ? 'Drop images here' : 'Drag & drop images here'}
              </p>
              <p className="text-sm mt-1">or click to select files</p>
              <p className="text-xs text-gray-400 mt-2">
                PNG, JPG, GIF, WebP • Max 5MB each • Multiple files supported
              </p>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && (
          <div className="p-4 border-b">
            <BulkUploadProgress 
              uploads={uploads} 
              onComplete={handleUploadComplete} 
            />
          </div>
        )}

        {/* Search & View Toggle */}
        <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search images..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#00204E] text-white' : 'bg-gray-100'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#00204E] text-white' : 'bg-gray-100'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          <button onClick={loadFiles} className="p-2 hover:bg-gray-200 rounded" title="Refresh">
            <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Files Grid/List */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <ImageIcon className="w-12 h-12 mb-2" />
              <p className="font-medium">No images found</p>
              <p className="text-sm">Click "Upload Images" to add images to your library</p>
              <button
                onClick={() => setShowUploadArea(true)}
                className="mt-3 px-4 py-2 bg-[#00204E] text-white rounded-lg text-sm font-medium hover:bg-[#003366]"
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload Images
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
              {filteredFiles.map((file) => (
                <button
                  key={file.file_id}
                  onClick={() => {
                    onSelect(`${API}/files/${file.file_id}`);
                    onClose();
                  }}
                  className="aspect-square border rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all group relative"
                >
                  <img
                    src={`${API}/files/${file.file_id}`}
                    alt={file.alt_text || file.original_filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{file.original_filename}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredFiles.map((file) => (
                <button
                  key={file.file_id}
                  onClick={() => {
                    onSelect(`${API}/files/${file.file_id}`);
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-2 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <img
                    src={`${API}/files/${file.file_id}`}
                    alt={file.alt_text || file.original_filename}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.original_filename}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(file.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between text-sm text-gray-500">
          <span>{filteredFiles.length} image{filteredFiles.length !== 1 ? 's' : ''} in library</span>
          {filteredFiles.length > 0 && (
            <span className="text-xs">Click an image to select it</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Image Upload Component with Drag & Drop + Media Library
// ============================================
const ImageUploader = ({ value, onChange, label = "Image", showMediaLibrary = true }) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value ? 'url' : 'upload'); // 'upload', 'url', or 'library'
  const [isDragging, setIsDragging] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const uploadFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        onChange(response.data.url);
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-bold text-gray-700">{label}</label>
      
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            mode === 'upload' 
              ? 'bg-[#00204E] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Upload className="w-3 h-3 inline mr-1" />
          Upload
        </button>
        {showMediaLibrary && (
          <button
            type="button"
            onClick={() => setShowLibrary(true)}
            className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <FolderOpen className="w-3 h-3 inline mr-1" />
            Library
          </button>
        )}
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            mode === 'url' 
              ? 'bg-[#00204E] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Link2 className="w-3 h-3 inline mr-1" />
          URL
        </button>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <div
            ref={dropZoneRef}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`w-full p-6 border-2 border-dashed rounded-lg transition-all cursor-pointer flex flex-col items-center justify-center gap-2 ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 text-blue-600' 
                : uploading 
                  ? 'border-gray-300 bg-gray-50 cursor-wait' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600'
            }`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 animate-spin" />
                <span className="text-sm font-medium">Uploading...</span>
              </>
            ) : isDragging ? (
              <>
                <Upload className="w-10 h-10" />
                <span className="text-sm font-medium">Drop image here</span>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10" />
                <span className="text-sm font-medium">Drag & drop or click to upload</span>
                <span className="text-xs text-gray-400">PNG, JPG, WebP up to 5MB</span>
              </>
            )}
          </div>
        </div>
      ) : (
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full p-2 border rounded-lg"
        />
      )}

      {/* Preview */}
      {value && (
        <div className="mt-2 relative group inline-block">
          <img 
            src={value} 
            alt="Preview" 
            className="max-h-32 rounded-lg border object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove image"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Media Library Modal */}
      <MediaLibrary 
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onSelect={(url) => onChange(url)}
      />
    </div>
  );
};

// ============================================
// Block Type Definitions
// ============================================
const BLOCK_TYPES = [
  { type: 'text', icon: Type, label: 'Text / Rich Text', description: 'Paragraphs, headings, lists' },
  { type: 'image', icon: Image, label: 'Image', description: 'Single image with caption' },
  { type: 'youtube', icon: Youtube, label: 'YouTube Video', description: 'Embed YouTube video' },
  { type: 'game_card', icon: Trophy, label: 'Game Card', description: 'Team matchup card', priority: true },
  { type: 'button', icon: Link2, label: 'Button / CTA', description: 'Call-to-action button' },
  { type: 'divider', icon: LayoutGrid, label: 'Divider', description: 'Section separator' },
  { type: 'html', icon: Settings, label: 'Custom HTML', description: 'Raw HTML code' },
];

// ============================================
// Game Card Editor Component (Priority Block)
// ============================================
const GameCardEditor = ({ data, onChange }) => {
  const [cardData, setCardData] = useState(data || {
    team1_name: '', team1_abbr: '', team1_logo: '',
    team2_name: '', team2_abbr: '', team2_logo: '',
    date: '', time: '', location: '', network: '',
    watch_url: '', live_stats_url: '', is_featured: false, game_name: ''
  });

  const handleChange = (field, value) => {
    const newData = { ...cardData, [field]: value };
    setCardData(newData);
    onChange(newData);
  };

  // SWAC Team presets for quick selection
  const swacTeams = [
    { name: 'Jackson State', abbr: 'JKST', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2870.png' },
    { name: 'Alabama A&M', abbr: 'AAMU', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2010.png' },
    { name: 'Alabama State', abbr: 'ALST', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2011.png' },
    { name: 'Alcorn State', abbr: 'ALCN', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2029.png' },
    { name: 'Arkansas-Pine Bluff', abbr: 'UAPB', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2029.png' },
    { name: 'Bethune-Cookman', abbr: 'BCU', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2065.png' },
    { name: 'Florida A&M', abbr: 'FAMU', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/50.png' },
    { name: 'Grambling State', abbr: 'GRAM', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2755.png' },
    { name: 'Mississippi Valley State', abbr: 'MVSU', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2400.png' },
    { name: 'Prairie View A&M', abbr: 'PV', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2504.png' },
    { name: 'Southern', abbr: 'SOU', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2582.png' },
    { name: 'Texas Southern', abbr: 'TXSO', logo: 'https://a.espncdn.com/i/teamlogos/ncaa/500/2640.png' },
  ];

  const selectTeam = (teamNum, team) => {
    const prefix = teamNum === 1 ? 'team1' : 'team2';
    const newData = {
      ...cardData,
      [`${prefix}_name`]: team.name,
      [`${prefix}_abbr`]: team.abbr,
      [`${prefix}_logo`]: team.logo
    };
    setCardData(newData);
    onChange(newData);
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-[#00204E] flex items-center gap-2">
          <Trophy className="w-5 h-5" />
          Game Card Editor
        </h4>
        <label className="flex items-center gap-2 text-sm">
          <input 
            type="checkbox" 
            checked={cardData.is_featured}
            onChange={(e) => handleChange('is_featured', e.target.checked)}
            className="rounded"
          />
          Featured Game
        </label>
      </div>

      {/* Game Name (optional) */}
      <input
        type="text"
        placeholder="Game Name (e.g., Gulf Coast Challenge)"
        value={cardData.game_name}
        onChange={(e) => handleChange('game_name', e.target.value)}
        className="w-full p-2 border rounded text-sm"
      />

      {/* Team Selection */}
      <div className="grid grid-cols-2 gap-4">
        {/* Team 1 */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase">Team 1 (Away)</label>
          <select 
            onChange={(e) => {
              const team = swacTeams.find(t => t.name === e.target.value);
              if (team) selectTeam(1, team);
            }}
            className="w-full p-2 border rounded text-sm"
            value={cardData.team1_name}
          >
            <option value="">Select SWAC Team</option>
            {swacTeams.map(t => (
              <option key={t.abbr} value={t.name}>{t.name}</option>
            ))}
            <option value="custom">Custom Team...</option>
          </select>
          <input
            type="text"
            placeholder="Team Name"
            value={cardData.team1_name}
            onChange={(e) => handleChange('team1_name', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Abbreviation"
            value={cardData.team1_abbr}
            onChange={(e) => handleChange('team1_abbr', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Logo URL"
            value={cardData.team1_logo}
            onChange={(e) => handleChange('team1_logo', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          {cardData.team1_logo && (
            <img src={cardData.team1_logo} alt="" className="w-12 h-12 object-contain mx-auto" />
          )}
        </div>

        {/* Team 2 */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase">Team 2 (Home)</label>
          <select 
            onChange={(e) => {
              const team = swacTeams.find(t => t.name === e.target.value);
              if (team) selectTeam(2, team);
            }}
            className="w-full p-2 border rounded text-sm"
            value={cardData.team2_name}
          >
            <option value="">Select SWAC Team</option>
            {swacTeams.map(t => (
              <option key={t.abbr} value={t.name}>{t.name}</option>
            ))}
            <option value="custom">Custom Team...</option>
          </select>
          <input
            type="text"
            placeholder="Team Name"
            value={cardData.team2_name}
            onChange={(e) => handleChange('team2_name', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Abbreviation"
            value={cardData.team2_abbr}
            onChange={(e) => handleChange('team2_abbr', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Logo URL"
            value={cardData.team2_logo}
            onChange={(e) => handleChange('team2_logo', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          {cardData.team2_logo && (
            <img src={cardData.team2_logo} alt="" className="w-12 h-12 object-contain mx-auto" />
          )}
        </div>
      </div>

      {/* Game Details */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase flex items-center gap-1">
            <Clock className="w-3 h-3" /> Date & Time
          </label>
          <input
            type="text"
            placeholder="Date (e.g., Sat, Oct 4)"
            value={cardData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Time (e.g., 5:00 PM CT)"
            value={cardData.time}
            onChange={(e) => handleChange('time', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Location & Network
          </label>
          <input
            type="text"
            placeholder="Location (e.g., Mobile, AL)"
            value={cardData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
          <input
            type="text"
            placeholder="Network (e.g., SWAC TV, ESPN+)"
            value={cardData.network}
            onChange={(e) => handleChange('network', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase flex items-center gap-1">
            <Tv className="w-3 h-3" /> Watch URL
          </label>
          <input
            type="url"
            placeholder="https://..."
            value={cardData.watch_url}
            onChange={(e) => handleChange('watch_url', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-600 uppercase flex items-center gap-1">
            <BarChart2 className="w-3 h-3" /> Live Stats URL
          </label>
          <input
            type="url"
            placeholder="https://..."
            value={cardData.live_stats_url}
            onChange={(e) => handleChange('live_stats_url', e.target.value)}
            className="w-full p-2 border rounded text-sm"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="mt-4 p-4 bg-white rounded-lg border-2 border-dashed border-gray-300">
        <p className="text-xs text-gray-500 text-center mb-2">Preview</p>
        <GameCardPreview data={cardData} />
      </div>
    </div>
  );
};

// Game Card Preview Component
const GameCardPreview = ({ data }) => {
  return (
    <div className={`border rounded-lg overflow-hidden ${data.is_featured ? 'border-yellow-400 border-2 bg-yellow-50/50' : 'border-gray-200 bg-white'}`}>
      {data.game_name && (
        <div className="bg-gradient-to-r from-[#0b4e9b] to-[#8e0e2c] text-white text-xs font-bold py-1 px-3 text-center">
          ⭐ {data.game_name}
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            <div className="flex flex-col items-center text-center min-w-[50px]">
              {data.team1_logo && <img src={data.team1_logo} alt="" className="w-8 h-8 object-contain" />}
              <span className="text-xs font-bold mt-1">{data.team1_abbr || data.team1_name?.slice(0,4) || '???'}</span>
            </div>
            <span className="text-gray-400 text-sm font-bold">@</span>
            <div className="flex flex-col items-center text-center min-w-[50px]">
              {data.team2_logo && <img src={data.team2_logo} alt="" className="w-8 h-8 object-contain" />}
              <span className="text-xs font-bold mt-1">{data.team2_abbr || data.team2_name?.slice(0,4) || '???'}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-bold text-[#00204E]">{data.time || 'TBD'}</div>
            <div className="text-xs text-gray-500">{data.date}</div>
            {data.network && (
              <div className="flex items-center justify-end gap-1 mt-1">
                <Tv className="w-3 h-3 text-gray-400" />
                <span className="text-xs font-medium text-gray-600">{data.network}</span>
              </div>
            )}
          </div>
        </div>
        {data.location && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-2 pt-2 border-t">
            <MapPin className="w-3 h-3" />
            {data.location}
          </div>
        )}
        <div className="flex gap-2 mt-2">
          {data.watch_url && (
            <span className="text-xs bg-[#00204E] text-white px-2 py-1 rounded">WATCH →</span>
          )}
          {data.live_stats_url && (
            <span className="text-xs border border-[#00204E] text-[#00204E] px-2 py-1 rounded">LIVE STATS →</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Block Editors for Different Types
// ============================================
const TextBlockEditor = ({ data, onChange }) => (
  <div className="space-y-2">
    <textarea
      value={data.content || ''}
      onChange={(e) => onChange({ ...data, content: e.target.value })}
      placeholder="Enter text content (supports basic HTML)"
      className="w-full p-3 border rounded-lg min-h-[150px] font-sans"
      rows={6}
    />
    <p className="text-xs text-gray-500">Supports HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a&gt;, &lt;ul&gt;, &lt;li&gt;</p>
  </div>
);

const ImageBlockEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <ImageUploader
      label="Image"
      value={data.url || ''}
      onChange={(url) => onChange({ ...data, url })}
      showMediaLibrary={true}
    />
    <input
      type="text"
      value={data.alt || ''}
      onChange={(e) => onChange({ ...data, alt: e.target.value })}
      placeholder="Alt text (for accessibility)"
      className="w-full p-2 border rounded"
    />
    <input
      type="text"
      value={data.caption || ''}
      onChange={(e) => onChange({ ...data, caption: e.target.value })}
      placeholder="Caption (optional)"
      className="w-full p-2 border rounded"
    />
    <input
      type="text"
      value={data.credit || ''}
      onChange={(e) => onChange({ ...data, credit: e.target.value })}
      placeholder="Photo credit (optional)"
      className="w-full p-2 border rounded"
    />
  </div>
);

const YouTubeBlockEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <input
      type="text"
      value={data.video_id || ''}
      onChange={(e) => onChange({ ...data, video_id: e.target.value })}
      placeholder="YouTube URL or Video ID (e.g., dQw4w9WgXcQ)"
      className="w-full p-2 border rounded"
    />
    <p className="text-xs text-gray-500">Paste a full YouTube URL or just the video ID</p>
    {data.video_id && (
      <div className="aspect-video bg-gray-100 rounded overflow-hidden">
        <iframe
          src={`https://www.youtube.com/embed/${data.video_id.includes('youtu') ? data.video_id.split('v=')[1]?.split('&')[0] || data.video_id.split('/').pop() : data.video_id}`}
          className="w-full h-full"
          frameBorder="0"
          allowFullScreen
          title="YouTube Preview"
        />
      </div>
    )}
  </div>
);

const ButtonBlockEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <input
      type="text"
      value={data.text || ''}
      onChange={(e) => onChange({ ...data, text: e.target.value })}
      placeholder="Button Text (e.g., WATCH NOW →)"
      className="w-full p-2 border rounded"
    />
    <input
      type="url"
      value={data.url || ''}
      onChange={(e) => onChange({ ...data, url: e.target.value })}
      placeholder="Button URL"
      className="w-full p-2 border rounded"
    />
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={data.new_tab || false}
        onChange={(e) => onChange({ ...data, new_tab: e.target.checked })}
      />
      Open in new tab
    </label>
    <select
      value={data.style || 'primary'}
      onChange={(e) => onChange({ ...data, style: e.target.value })}
      className="w-full p-2 border rounded"
    >
      <option value="primary">Primary (Blue)</option>
      <option value="secondary">Secondary (Outline)</option>
      <option value="accent">Accent (Orange)</option>
    </select>
  </div>
);

const HtmlBlockEditor = ({ data, onChange }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);
  
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const isZip = fileName.endsWith('.zip');
    const isHtml = fileName.endsWith('.html') || fileName.endsWith('.htm');
    
    if (!isZip && !isHtml) {
      toast.error('Please upload an HTML or ZIP file');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(isZip ? 'Extracting ZIP file...' : 'Reading HTML file...');
    
    try {
      if (isHtml) {
        // Read HTML file directly
        const reader = new FileReader();
        reader.onload = (event) => {
          const htmlContent = event.target.result;
          onChange({ ...data, html: htmlContent, source_file: file.name });
          setIsUploading(false);
          setUploadProgress('');
          toast.success(`HTML loaded from ${file.name}`);
        };
        reader.onerror = () => {
          toast.error('Error reading file');
          setIsUploading(false);
        };
        reader.readAsText(file);
      } else if (isZip) {
        // Upload ZIP to server for extraction
        setUploadProgress('Uploading ZIP file...');
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload/html-zip`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          onChange({ 
            ...data, 
            html: response.data.html, 
            source_file: file.name,
            extracted_files: response.data.files || []
          });
          toast.success(`ZIP extracted: ${response.data.files?.length || 0} files processed`);
        } else {
          toast.error(response.data.error || 'Failed to process ZIP');
        }
        setIsUploading(false);
        setUploadProgress('');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error processing file: ' + (error.response?.data?.detail || error.message));
      setIsUploading(false);
      setUploadProgress('');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="space-y-4">
      {/* File Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Upload HTML/ZIP File</span>
          </div>
          {data.source_file && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              Source: {data.source_file}
            </span>
          )}
        </div>
        
        <div className="flex gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,.zip"
            onChange={handleFileUpload}
            className="hidden"
            id="html-file-upload"
            data-testid="html-file-upload-input"
          />
          <label
            htmlFor="html-file-upload"
            data-testid="html-file-upload-button"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
              isUploading 
                ? 'bg-blue-50 border-blue-300 text-blue-600' 
                : 'bg-white border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-700'
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{uploadProgress || 'Processing...'}</span>
              </>
            ) : (
              <>
                <FolderOpen className="w-5 h-5" />
                <span>Choose HTML or ZIP File</span>
              </>
            )}
          </label>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Upload a single .html file or a .zip containing HTML with images/CSS. ZIP files will be extracted and processed.
        </p>
      </div>
      
      {/* HTML Code Editor */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">HTML Code</label>
          <span className="text-xs text-gray-500">
            {(data.html || '').length.toLocaleString()} characters
          </span>
        </div>
        <textarea
          value={data.html || ''}
          onChange={(e) => onChange({ ...data, html: e.target.value })}
          placeholder="Enter raw HTML code or upload a file above"
          className="w-full p-3 border rounded-lg min-h-[250px] font-mono text-sm bg-gray-900 text-green-400"
          rows={12}
          spellCheck={false}
          data-testid="html-code-textarea"
        />
      </div>
      
      {/* Preview Toggle */}
      {data.html && (
        <details className="border rounded-lg">
          <summary className="px-4 py-2 bg-gray-50 cursor-pointer font-medium text-sm text-gray-700 hover:bg-gray-100">
            Preview HTML ({Math.round((data.html || '').length / 1024)}KB)
          </summary>
          <div className="p-4 max-h-[400px] overflow-auto border-t">
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: data.html || '' }}
            />
          </div>
        </details>
      )}
      
      <p className="text-xs text-amber-600 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        Advanced: Use with caution. Invalid HTML may break the article layout.
      </p>
    </div>
  );
};

// ============================================
// Single Block Component
// ============================================
const BlockItem = ({ block, index, totalBlocks, onUpdate, onDelete, onMove, onDuplicate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [blockData, setBlockData] = useState(block.data || {});
  
  const blockType = BLOCK_TYPES.find(t => t.type === block.type);
  const Icon = blockType?.icon || Type;

  const handleSave = () => {
    onUpdate(block.block_id, blockData);
    setIsEditing(false);
    toast.success('Block updated');
  };

  const handleDataChange = (newData) => {
    setBlockData(newData);
    // Keep uploaded/edited block data in the article draft immediately so it
    // cannot be lost if the editor is saved before this panel is closed.
    onUpdate(block.block_id, newData);
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'text': return <TextBlockEditor data={blockData} onChange={handleDataChange} />;
      case 'image': return <ImageBlockEditor data={blockData} onChange={handleDataChange} />;
      case 'youtube': return <YouTubeBlockEditor data={blockData} onChange={handleDataChange} />;
      case 'game_card': return <GameCardEditor data={blockData} onChange={handleDataChange} />;
      case 'button': return <ButtonBlockEditor data={blockData} onChange={handleDataChange} />;
      case 'html': return <HtmlBlockEditor data={blockData} onChange={handleDataChange} />;
      case 'divider': return <div className="text-center text-gray-500 py-4">— Divider —</div>;
      default: return <p className="text-gray-500">Unknown block type: {block.type}</p>;
    }
  };

  const renderPreview = () => {
    switch (block.type) {
      case 'text': return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: blockData.content || '<em>Empty text block</em>' }} />;
      case 'image': return blockData.url ? <img src={blockData.url} alt={blockData.alt || ''} className="max-h-32 object-contain" /> : <span className="text-gray-400">No image</span>;
      case 'youtube': return <span className="text-gray-600">📹 YouTube: {blockData.video_id || 'No video'}</span>;
      case 'game_card': return <GameCardPreview data={blockData} />;
      case 'button': return <span className="inline-block px-3 py-1 bg-[#00204E] text-white rounded text-sm">{blockData.text || 'Button'}</span>;
      case 'divider': return <hr className="border-t-2 border-gray-300" />;
      case 'html': return <span className="text-gray-600 font-mono text-xs">&lt;HTML&gt; {(blockData.html || '').slice(0, 50)}...</span>;
      default: return null;
    }
  };

  return (
    <div className={`border rounded-lg bg-white shadow-sm ${isEditing ? 'ring-2 ring-blue-500' : ''}`}>
      {/* Block Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b rounded-t-lg">
        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
        <Icon className="w-4 h-4 text-[#00204E]" />
        <span className="font-medium text-sm flex-1">{blockType?.label || block.type}</span>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => onMove(index, -1)}
            disabled={index === 0}
            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
            title="Move up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove(index, 1)}
            disabled={index === totalBlocks - 1}
            className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
            title="Move down"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-1 hover:bg-gray-200 rounded"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDuplicate(index)}
            className="p-1 hover:bg-gray-200 rounded"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              if (window.confirm('Delete this block?')) onDelete(block.block_id);
            }}
            className="p-1 hover:bg-red-100 rounded text-red-600"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Block Content */}
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-3">
            {renderEditor()}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Check className="w-4 h-4 mr-1" /> Save Block
              </Button>
            </div>
          </div>
        ) : (
          <div className="cursor-pointer" onClick={() => setIsEditing(true)}>
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Add Block Button
// ============================================
const AddBlockButton = ({ onAdd }) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
      >
        <Plus className="w-5 h-5" />
        Add Block
      </button>
      
      {showMenu && (
        <div className="absolute z-10 w-full mt-2 bg-white border rounded-lg shadow-lg p-2 grid grid-cols-2 gap-1">
          {BLOCK_TYPES.map((type) => (
            <button
              key={type.type}
              onClick={() => {
                onAdd(type.type);
                setShowMenu(false);
              }}
              className={`flex items-start gap-2 p-2 rounded hover:bg-gray-100 text-left ${type.priority ? 'bg-yellow-50 border border-yellow-200' : ''}`}
            >
              <type.icon className={`w-5 h-5 mt-0.5 ${type.priority ? 'text-yellow-600' : 'text-gray-600'}`} />
              <div>
                <div className="font-medium text-sm">{type.label}</div>
                <div className="text-xs text-gray-500">{type.description}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Interactive Article Editor Component
// ============================================
const InteractiveArticleEditor = ({ articleId, onClose, onSave }) => {
  const [article, setArticle] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [persistedArticleId, setPersistedArticleId] = useState(articleId || null);
  const [activeTab, setActiveTab] = useState('edit'); // edit, preview, seo, settings
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile

  // Load article
  useEffect(() => {
    const loadArticle = async () => {
      if (!articleId) {
        // New article
        setArticle({
          title: '',
          subtitle: '',
          category: 'SWAC Football',
          author: 'SheLovesThee Team',
          featured_image: '',
          status: 'draft',
          seo: {},
          homepage: {}
        });
        setBlocks([]);
        setPersistedArticleId(null);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/interactive-articles/${articleId}`);
        setArticle(response.data);
        setBlocks(response.data.blocks || []);
        setPersistedArticleId(response.data.article_id);
      } catch (error) {
        toast.error('Failed to load article');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [articleId]);

  // Add new block
  const addBlock = (type) => {
    const newBlock = {
      block_id: `blk_${Date.now().toString(36)}`,
      type,
      data: type === 'game_card' ? {
        team1_name: '', team1_abbr: '', team1_logo: '',
        team2_name: '', team2_abbr: '', team2_logo: '',
        date: '', time: '', location: '', network: '',
        watch_url: '', live_stats_url: '', is_featured: false
      } : {},
      order: blocks.length
    };
    setBlocks([...blocks, newBlock]);
  };

  // Update block
  const updateBlock = (blockId, newData) => {
    setBlocks(currentBlocks => currentBlocks.map(b =>
      b.block_id === blockId ? { ...b, data: newData } : b
    ));
  };

  // Delete block
  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.block_id !== blockId));
    toast.success('Block deleted');
  };

  // Move block
  const moveBlock = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    setBlocks(newBlocks);
  };

  // Duplicate block
  const duplicateBlock = (index) => {
    const block = blocks[index];
    const newBlock = {
      ...block,
      block_id: `blk_${Date.now().toString(36)}`,
      data: { ...block.data }
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    setBlocks(newBlocks);
    toast.success('Block duplicated');
  };

  // Save article
  const handleSave = async (publish = false) => {
    if (!article.title) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...article,
        blocks: blocks.map((b, i) => ({ ...b, order: i })),
        status: publish ? 'draft' : article.status
      };

      let response;
      const saveTargetId = persistedArticleId || articleId;
      if (saveTargetId) {
        response = await axios.put(`${API}/interactive-articles/${saveTargetId}`, payload);
      } else {
        response = await axios.post(`${API}/interactive-articles`, payload);
      }

      const savedArticle = response.data.article;
      setArticle(savedArticle);
      setBlocks(savedArticle.blocks || blocks);
      setPersistedArticleId(savedArticle.article_id);

      if (publish) {
        try {
          const publishResult = await axios.post(`${API}/interactive-articles/${savedArticle.article_id}/publish`);
          toast.success('Article published!');
          if (publishResult.data.warning) toast.warning(publishResult.data.warning);
        } catch (publishError) {
          const detail = publishError.response?.data?.detail || 'The draft was saved, but publication could not finish.';
          toast.error(`Draft saved. ${detail}`);
          return;
        }
      } else {
        toast.success('Article saved!');
      }

      if (onSave) onSave(savedArticle);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(item => item.msg).join(' ') : detail;
      toast.error(message || 'Failed to save article');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00204E]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="font-bold text-lg">{articleId ? 'Edit Article' : 'New Interactive Article'}</h2>
            <p className="text-sm text-gray-500">
              {article.status === 'draft' ? '📝 Draft' : article.status === 'published' ? '✅ Published' : '📅 Scheduled'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="interactive-save-draft-button">
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button variant="outline" onClick={() => setActiveTab('preview')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="interactive-publish-button">
            <Send className="w-4 h-4 mr-2" />
            {article.status === 'published' ? 'Update' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {['edit', 'preview', 'seo', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize ${activeTab === tab ? 'text-[#00204E] border-b-2 border-[#00204E]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="p-4">
        {activeTab === 'edit' && (
          <div className="space-y-4">
            {/* Article Metadata */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={article.title}
                  onChange={(e) => setArticle({ ...article, title: e.target.value })}
                  placeholder="Article Title"
                  className="w-full p-3 border rounded-lg text-lg font-bold"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={article.subtitle || ''}
                  onChange={(e) => setArticle({ ...article, subtitle: e.target.value })}
                  placeholder="Article Subtitle"
                  className="w-full p-3 border rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
                <select
                  value={article.category}
                  onChange={(e) => setArticle({ ...article, category: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option>SWAC Football</option>
                  <option>Basketball</option>
                  <option>Baseball</option>
                  <option>Soccer</option>
                  <option>Track & Field</option>
                  <option>HBCU</option>
                  <option>Jackson State</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Author</label>
                <input
                  type="text"
                  value={article.author}
                  onChange={(e) => setArticle({ ...article, author: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
              <div>
                <ImageUploader
                  label="Featured Image"
                  value={article.featured_image || ''}
                  onChange={(url) => setArticle({ ...article, featured_image: url })}
                />
              </div>
            </div>

            {/* Blocks Editor */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-bold text-lg mb-3">Content Blocks</h3>
              
              <div className="space-y-3">
                {blocks.map((block, index) => (
                  <BlockItem
                    key={block.block_id}
                    block={block}
                    index={index}
                    totalBlocks={blocks.length}
                    onUpdate={updateBlock}
                    onDelete={deleteBlock}
                    onMove={moveBlock}
                    onDuplicate={duplicateBlock}
                  />
                ))}
              </div>

              <div className="mt-4">
                <AddBlockButton onAdd={addBlock} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="space-y-4">
            {/* Preview Mode Toggle */}
            <div className="flex justify-center gap-2">
              {['desktop', 'tablet', 'mobile'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setPreviewMode(mode)}
                  className={`px-4 py-2 rounded capitalize ${previewMode === mode ? 'bg-[#00204E] text-white' : 'bg-gray-100'}`}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* Preview Frame */}
            <div className={`mx-auto border rounded-lg overflow-auto bg-white ${
              previewMode === 'desktop' ? 'max-w-4xl' : 
              previewMode === 'tablet' ? 'max-w-lg' : 'max-w-sm'
            }`}>
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-2">{article.title || 'Untitled'}</h1>
                {article.subtitle && <p className="text-gray-600 mb-4">{article.subtitle}</p>}
                <p className="text-sm text-gray-500 mb-6">By {article.author}</p>
                
                {blocks.map((block, i) => (
                  <div key={block.block_id} className="mb-4">
                    {block.type === 'text' && (
                      <div dangerouslySetInnerHTML={{ __html: block.data?.content || '' }} />
                    )}
                    {block.type === 'image' && block.data?.url && (
                      <figure>
                        <img src={block.data.url} alt={block.data.alt || ''} className="w-full rounded" />
                        {block.data.caption && <figcaption className="text-sm text-gray-500 mt-1">{block.data.caption}</figcaption>}
                      </figure>
                    )}
                    {block.type === 'youtube' && block.data?.video_id && (
                      <div className="aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${block.data.video_id}`}
                          className="w-full h-full rounded"
                          allowFullScreen
                          title="Video"
                        />
                      </div>
                    )}
                    {block.type === 'game_card' && (
                      <GameCardPreview data={block.data} />
                    )}
                    {block.type === 'button' && (
                      <div className="text-center">
                        <a href={block.data?.url || '#'} className="inline-block px-6 py-3 bg-[#00204E] text-white rounded-lg font-bold">
                          {block.data?.text || 'Button'}
                        </a>
                      </div>
                    )}
                    {block.type === 'divider' && <hr className="my-6" />}
                    {block.type === 'html' && (
                      <div dangerouslySetInnerHTML={{ __html: block.data?.html || '' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="max-w-2xl space-y-4">
            <h3 className="font-bold text-lg">SEO Settings</h3>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                SEO Title <span className="text-gray-400 font-normal">({(article.seo?.title || article.title || '').length}/60)</span>
              </label>
              <input
                type="text"
                value={article.seo?.title || article.title || ''}
                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, title: e.target.value } })}
                className="w-full p-2 border rounded"
                maxLength={60}
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Meta Description <span className="text-gray-400 font-normal">({(article.seo?.meta_description || '').length}/160)</span>
              </label>
              <textarea
                value={article.seo?.meta_description || ''}
                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, meta_description: e.target.value } })}
                className="w-full p-2 border rounded"
                rows={3}
                maxLength={160}
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">URL Slug</label>
              <input
                type="text"
                value={article.seo?.slug || ''}
                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') } })}
                placeholder="how-to-watch-swac-football"
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Primary Keyword</label>
              <input
                type="text"
                value={article.seo?.primary_keyword || ''}
                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, primary_keyword: e.target.value } })}
                placeholder="how to watch SWAC football"
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Social/OG Image URL</label>
              <input
                type="url"
                value={article.seo?.social_image || article.featured_image || ''}
                onChange={(e) => setArticle({ ...article, seo: { ...article.seo, social_image: e.target.value } })}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl space-y-6">
            <div>
              <h3 className="font-bold text-lg mb-3">Homepage Feature</h3>
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={article.homepage?.is_featured || false}
                  onChange={(e) => setArticle({ 
                    ...article, 
                    homepage: { ...article.homepage, is_featured: e.target.checked } 
                  })}
                  className="rounded"
                />
                <span className="font-medium">Feature on Homepage</span>
              </label>
              
              {article.homepage?.is_featured && (
                <div className="ml-6 space-y-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <input
                    type="text"
                    value={article.homepage?.homepage_headline || ''}
                    onChange={(e) => setArticle({ ...article, homepage: { ...article.homepage, homepage_headline: e.target.value } })}
                    placeholder="Homepage Headline"
                    className="w-full p-2 border rounded"
                  />
                  <input
                    type="text"
                    value={article.homepage?.homepage_summary || ''}
                    onChange={(e) => setArticle({ ...article, homepage: { ...article.homepage, homepage_summary: e.target.value } })}
                    placeholder="Homepage Summary"
                    className="w-full p-2 border rounded"
                  />
                  <input
                    type="text"
                    value={article.homepage?.cta_text || ''}
                    onChange={(e) => setArticle({ ...article, homepage: { ...article.homepage, cta_text: e.target.value } })}
                    placeholder="CTA Text (e.g., WATCH + FOLLOW LIVE →)"
                    className="w-full p-2 border rounded"
                  />
                </div>
              )}
            </div>

            <div>
              <h3 className="font-bold text-lg mb-3">Source & Credits</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={article.source_name || ''}
                  onChange={(e) => setArticle({ ...article, source_name: e.target.value })}
                  placeholder="Source Name"
                  className="p-2 border rounded"
                />
                <input
                  type="url"
                  value={article.source_url || ''}
                  onChange={(e) => setArticle({ ...article, source_url: e.target.value })}
                  placeholder="Source URL"
                  className="p-2 border rounded"
                />
                <input
                  type="text"
                  value={article.photo_credit || ''}
                  onChange={(e) => setArticle({ ...article, photo_credit: e.target.value })}
                  placeholder="Photo Credit"
                  className="p-2 border rounded"
                />
                <input
                  type="text"
                  value={article.video_credit || ''}
                  onChange={(e) => setArticle({ ...article, video_credit: e.target.value })}
                  placeholder="Video Credit"
                  className="p-2 border rounded"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveArticleEditor;
